import { useEffect, useState } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  Users,
  ShieldCheck,
  Building2,
  Monitor,
  Award,
  CalendarDays,
  MessageSquare,
  BarChart3,
  LogOut,
  ChevronLeft,
  ChevronRight,
  Link2,
  UserCog,
  UserCheck,
  Contact,
  Inbox,
  Menu,
  X,
} from 'lucide-react';
import { useAuthStore } from '@/stores/auth';
import { useLogout } from '@/hooks/useAuth';

interface NavItem {
  label: string;
  path: string;
  icon: React.ElementType;
}

const navItems: NavItem[] = [
  { label: 'Dashboard', path: '/dashboard', icon: LayoutDashboard },
  { label: 'Utilizadores', path: '/users', icon: Users },
  { label: 'Presenças', path: '/presencas', icon: UserCheck },
  { label: 'Consentimentos EE', path: '/parental-consent', icon: ShieldCheck },
  { label: 'Encarregados de Educação', path: '/encarregados', icon: Contact },
  { label: 'Pedidos de privacidade', path: '/pedidos-privacidade', icon: Inbox },
  { label: 'Centros Digitais', path: '/centros-digitais', icon: Building2 },
  { label: 'Equipamentos', path: '/equipamentos', icon: Monitor },
  { label: 'Credenciais', path: '/credenciais', icon: Award },
  { label: 'Atividades', path: '/atividades', icon: CalendarDays },
  { label: 'Mensagens', path: '/mensagens', icon: MessageSquare },
  { label: 'Equipamentos utilizados por Utilizadores', path: '/equipamentos-utilizadores', icon: Link2 },
  { label: 'Administradores', path: '/admin-users', icon: UserCog },
  { label: 'Relatórios / KPIs', path: '/reports', icon: BarChart3 },
];

const SIDEBAR_BG = '#512f89';

export default function AdminLayout() {
  const { user } = useAuthStore();
  const logout = useLogout();
  const navigate = useNavigate();
  const location = useLocation();
  // Collapsing is for a wide screen; below lg the menu is a drawer instead.
  const [collapsed, setCollapsed] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  // The drawer closes once a page is chosen, and on Escape.
  useEffect(() => setMenuOpen(false), [location.pathname]);
  useEffect(() => {
    if (!menuOpen) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setMenuOpen(false);
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [menuOpen]);

  const handleLogout = () => {
    logout.mutate(undefined, {
      onSuccess: () => navigate('/login'),
    });
  };

  return (
    <div className="flex flex-col min-h-screen">
      {/* ── Top Bar ─────────────────────────────────────────────────────────── */}
      <header className="fixed top-0 left-0 right-0 z-30 h-16 bg-card border-b border-border shadow-sm flex items-center gap-3 px-4 lg:px-6">
        <button
          type="button"
          onClick={() => setMenuOpen(!menuOpen)}
          className="lg:hidden -ml-1 p-2 rounded-md text-muted-foreground hover:bg-accent hover:text-foreground transition-colors"
          aria-label={menuOpen ? 'Fechar menu' : 'Abrir menu'}
          aria-expanded={menuOpen}
          aria-controls="admin-sidebar"
        >
          {menuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>

        {/* Logo — left */}
        <div className="flex items-center gap-2 lg:w-56 shrink-0">
          <div className="w-8 h-8 rounded-md flex items-center justify-center font-bold text-sm text-white" style={{ backgroundColor: SIDEBAR_BG }}>
            D
          </div>
          <span className="text-lg font-bold tracking-tight" style={{ color: SIDEBAR_BG }}>DIGGER</span>
        </div>

        {/* App name — centre */}
        <div className="flex-1 hidden md:flex justify-center">
          <span className="text-base font-semibold text-foreground tracking-wide">
            Plataforma de Administração
          </span>
        </div>

        {/* User + Logout — right */}
        <div className="flex items-center gap-4 ml-auto md:ml-0 lg:w-56 justify-end min-w-0">
          <span className="hidden sm:inline text-sm text-muted-foreground font-medium truncate max-w-[160px]">
            {user?.name || user?.email}
          </span>
          <button
            onClick={handleLogout}
            disabled={logout.isPending}
            className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-destructive transition-colors disabled:opacity-50"
            title="Terminar sessão"
          >
            <LogOut className="w-4 h-4" />
            <span className="sr-only">Terminar sessão</span>
          </button>
        </div>
      </header>

      {/* ── Body (sidebar + content) ─────────────────────────────────────────── */}
      <div className="flex flex-1 pt-16 lg:pb-10">
        {/* Below lg the sidebar is a drawer over the page, behind a scrim. */}
        {menuOpen && (
          <div
            className="fixed inset-0 top-16 z-30 bg-black/40 lg:hidden"
            onClick={() => setMenuOpen(false)}
            aria-hidden="true"
          />
        )}

        {/* ── Sidebar ──────────────────────────────────────────────────────── */}
        <aside
          id="admin-sidebar"
          className={`fixed top-16 bottom-0 lg:bottom-10 left-0 z-40 lg:z-20 flex flex-col border-r w-64 transition-all duration-200 ${
            menuOpen ? 'translate-x-0' : '-translate-x-full invisible'
          } lg:visible lg:translate-x-0 ${collapsed ? 'lg:w-16' : 'lg:w-60'}`}
          style={{ backgroundColor: SIDEBAR_BG, borderRightColor: 'rgba(255,255,255,0.1)' }}
        >
          {/* Toggle button */}
          <button
            onClick={() => setCollapsed(!collapsed)}
            className="hidden lg:flex items-center justify-center h-10 w-full shrink-0 transition-colors"
            style={{ borderBottom: '1px solid rgba(255,255,255,0.12)', color: 'rgba(255,255,255,0.6)' }}
            onMouseEnter={e => (e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.08)')}
            onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'transparent')}
            title={collapsed ? 'Expandir menu' : 'Colapsar menu'}
            aria-label={collapsed ? 'Expandir menu' : 'Colapsar menu'}
          >
            {collapsed ? (
              <ChevronRight className="w-4 h-4" />
            ) : (
              <>
                <ChevronLeft className="w-4 h-4 mr-1" />
                <span className="text-xs font-medium">Colapsar</span>
              </>
            )}
          </button>

          {/* Nav items */}
          <nav className="flex-1 overflow-y-auto py-2" aria-label="Menu principal">
            {navItems.map((item) => {
              // An item stays lit on the pages beneath it (/atividades/12).
              const isActive =
                location.pathname === item.path ||
                location.pathname.startsWith(`${item.path}/`);
              const Icon = item.icon;

              return (
                <NavLink
                  key={item.path}
                  to={item.path}
                  style={isActive
                    ? { backgroundColor: 'rgba(255,255,255,0.2)', color: '#ffffff' }
                    : { color: 'rgba(255,255,255,0.72)' }
                  }
                  className="flex items-center gap-3 px-4 py-2.5 mx-2 my-0.5 rounded-md text-sm font-medium transition-colors hover:bg-white/10 hover:text-white"
                  title={collapsed ? item.label : undefined}
                >
                  <Icon className="w-5 h-5 shrink-0" />
                  <span className={`truncate ${collapsed ? 'lg:hidden' : ''}`}>{item.label}</span>
                </NavLink>
              );
            })}
          </nav>

          {/* Bottom logo */}
          <div
            className={`shrink-0 flex items-center p-3 ${collapsed ? 'lg:justify-center' : ''}`}
            style={{ borderTop: '1px solid rgba(255,255,255,0.12)' }}
          >
            <img
              src="/IMG_0243.PNG"
              alt="DIGGER logo"
              className={`object-contain transition-all duration-200 h-20 lg:h-32 max-w-full ${collapsed ? 'lg:w-9 lg:h-9' : ''}`}
            />
          </div>
        </aside>

        {/* ── Main content ─────────────────────────────────────────────────── */}
        <main
          id="main-content"
          className={`flex-1 min-w-0 overflow-auto transition-all duration-200 ${
            collapsed ? 'lg:ml-16' : 'lg:ml-60'
          }`}
        >
          <div className="p-4 sm:p-6 lg:p-8">
            <Outlet />
          </div>
        </main>
      </div>

      {/* ── Footer ───────────────────────────────────────────────────────────── */}
      <footer
        className="lg:fixed lg:bottom-0 lg:left-0 lg:right-0 z-20 py-3 px-4 lg:py-0 lg:h-10 bg-card border-t border-border flex items-center justify-center"
      >
        <p className="text-xs text-muted-foreground text-center">
          © {new Date().getFullYear()} Associação para a Inclusão Digital. Todos os direitos reservados.
        </p>
      </footer>
    </div>
  );
}
